import { SDKOptions } from '@sda/sdk/helpers/sdk.options';
import { Acts } from './acts';
import { Categories } from './categories';
import { Reviews } from './reviews';
import { Permissions } from './permissions';
import { Queues } from './queues';
import { Shows } from './shows';
import { Tickets } from './tickets';
import { Users } from './users';
import { Venues } from './venues';
import { Links } from './links';
import { Beacons } from './beacons';
import { Feedback } from './feedback';
import { Messages } from './messages';
import { Threads } from './threads';
import { Notifications } from './notifications';
import { Invitations } from './invitations';
import { Payouts } from './payouts';
import { Settings } from './settings';
import { Orders } from './orders';
import { Requests } from './requests';
import { SDKFactory } from './helpers/sdk.factory';
import { Products } from './products';
import { Profiles } from './profiles';
import type { Reducers } from '@sda/enums/store';
import type { BaseHelpers } from './helpers/firestore.helpers/base.helpers';
import type { SDK } from './helpers/sdk';
import type { FirestoreClientData } from './interfaces';

/**
 * SideDoor API SDK.
 * Using a system like this to wrap function calls to Firebase etc. means more flexibility in the future if/when
 * data structures change, or system transitions are made.
 */
export class BaseSDK {
  public fsh: BaseHelpers;

  // Endpoints - Define them here as well as in the constructor
  public Acts: Acts;

  public Links: Links;

  public Categories: Categories;

  public Permissions: Permissions;

  public Profiles: Profiles;

  public Queues: Queues;

  public Shows: Shows;

  public Beacons: Beacons;

  public Feedback: Feedback;

  public Tickets: Tickets;

  public Users: Users;

  public Venues: Venues;

  public Reviews: Reviews;

  public Settings: Settings;

  public Messages: Messages;

  public Threads: Threads;

  public Notifications: Notifications;

  public Orders: Orders;

  public Invitations: Invitations;

  public Payouts: Payouts;

  public Requests: Requests;

  public Products: Products;

  constructor(apiClient: FirestoreClientData) {
    this.fsh = new SDKOptions.BaseFirestoreHelpers(apiClient);

    this.Acts = new Acts(apiClient);
    this.Links = new Links(apiClient);
    this.Categories = new Categories(apiClient);
    this.Permissions = new Permissions(apiClient);
    this.Queues = new Queues(apiClient);
    this.Shows = new Shows(apiClient);
    this.Beacons = new Beacons(apiClient);
    this.Feedback = new Feedback(apiClient);
    this.Tickets = new Tickets(apiClient);
    this.Users = new Users(apiClient);
    this.Venues = new Venues(apiClient);
    this.Reviews = new Reviews(apiClient);
    this.Messages = new Messages(apiClient);
    this.Threads = new Threads(apiClient);
    this.Notifications = new Notifications(apiClient);
    this.Invitations = new Invitations(apiClient);
    this.Settings = new Settings(apiClient);
    this.Orders = new Orders(apiClient);
    this.Profiles = new Profiles(apiClient);

    this.Payouts = new Payouts(apiClient);
    this.Requests = new Requests(apiClient);

    this.Products = new Products(apiClient);
  }

  /**
   * Retrieves a collection group by name
   * @param  {string} collectionName
   * @return {FirestoreBaseClass}
   */
  getCollectionGroup<T>(collectionName: string, reducerName?: Reducers): SDK<T> {
    const { client, clientData, methods } = this.fsh;

    return SDKFactory<T>(
      clientData,
      this.fsh.withConverter<T>(methods.collectionGroup(client, collectionName) as any),
      { reducerName: reducerName || null },
    );
  }
}
